import { useRef, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowDown, Check, Sparkles, HeartHandshake, Gem, Hourglass } from "lucide-react";
import { IMG } from "@/lib/images";
import { UNIVERSE_META, PROJECTS, TESTI_IMG, TESTI_META } from "@/lib/content";
import { useLang } from "@/i18n/LanguageContext";
import api from "@/lib/api";
import { useSiteConfig } from "@/context/SiteConfigContext";
import { Reveal, MaskLines, FadeIn } from "@/components/Reveal";
import { EditorialMarquee } from "@/components/Marquee";
import { ConsultationForm } from "@/components/ConsultationForm";
import { BookConsultation } from "@/components/BookConsultation";
import { SEO } from "@/components/SEO";
import { pageSEO, businessSchema } from "@/lib/seo";

const Hero = () => {
  const { c } = useLang();
  const { config } = useSiteConfig();
  const hasCalendly = !!(config.calendly_url || "").trim();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const overlay = useTransform(scrollYProgress, [0, 1], [0.35, 0.7]);

  return (
    <section ref={ref} data-testid="hero" className="relative h-screen overflow-hidden grain">
      <motion.div style={{ y, scale }} className="absolute inset-0 will-change-transform">
        <img src={IMG.hero} alt="O'Lime Design" className="w-full h-full object-cover" />
      </motion.div>
      <motion.div style={{ opacity: overlay }} className="absolute inset-0 bg-black" />
      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-black/30" />

      <div className="relative z-10 h-full container-olime flex flex-col justify-center text-white">
        <FadeIn delay={0.1}><span className="eyebrow text-white/70">{c.hero.eyebrow}</span></FadeIn>
        <h1 className="font-serif text-[15vw] md:text-[11vw] lg:text-[9.5vw] leading-[0.86] tracking-tight mt-4 !text-white">
          <MaskLines lines={["O\u2019LIME", "DESIGN"]} base={0.3} stagger={0.14} />
        </h1>
        <FadeIn delay={0.9} className="mt-8 max-w-xl">
          <p className="text-base md:text-lg font-light text-white/85 leading-relaxed">{c.hero.phrase}</p>
        </FadeIn>
        <FadeIn delay={1.1} className="mt-10 flex flex-wrap gap-4">
          <Link to="/portfolio" data-testid="hero-projects-cta" className="btn-light">{c.cta.discoverProjects} <ArrowRight size={14} /></Link>
          {hasCalendly ? (
            <BookConsultation variant="light" testId="hero-book-cta" />
          ) : (
            <Link to="/contact#form" data-testid="hero-book-cta" className="btn-light">{c.cta.makeAppointment}</Link>
          )}
        </FadeIn>
      </div>

      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.6, duration: 1 }} className="absolute bottom-8 left-1/2 -translate-x-1/2 text-white/60">
        <ArrowDown size={18} className="animate-bounce" />
      </motion.div>
    </section>
  );
};

const Vision = () => {
  const { c } = useLang();
  return (
    <section data-testid="vision" className="py-24 lg:py-36 bg-olime-bg">
      <div className="container-olime grid grid-cols-1 lg:grid-cols-2 gap-14 lg:gap-24 items-center">
        <Reveal>
          <div className="relative overflow-hidden">
            <img src={IMG.vision} alt={c.vision.eyebrow} className="w-full aspect-[4/5] object-cover" />
            <div className="absolute inset-4 border border-white/40 pointer-events-none" />
          </div>
        </Reveal>
        <div>
          <Reveal><span className="eyebrow">{c.vision.eyebrow}</span></Reveal>
          <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.02] mt-5 tracking-tight">
            <MaskLines lines={[c.vision.title1, c.vision.title2]} animate={false} lineClassName="italic" />
          </h2>
          <Reveal delay={0.15}>
            <div className="mt-8 space-y-5 text-olime-muted leading-relaxed max-w-xl">
              <p>{c.vision.p1}</p><p>{c.vision.p2}</p><p>{c.vision.p3}</p>
            </div>
          </Reveal>
          <Reveal delay={0.25}>
            <div className="mt-10 grid grid-cols-2 gap-4">
              {c.vision.pictos.map((p) => (
                <div key={p} className="flex items-center gap-3 text-sm text-olime-ink">
                  <span className="flex items-center justify-center w-6 h-6 rounded-full bg-olime-sand/40"><Check size={13} /></span>{p}
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

const Universes = () => {
  const { c } = useLang();
  return (
    <section data-testid="universes" className="py-24 lg:py-32 bg-white">
      <div className="container-olime">
        <div className="mb-14">
          <Reveal><span className="eyebrow">{c.universes.eyebrow}</span>
            <h2 className="font-serif text-4xl md:text-6xl tracking-tight mt-3">{c.universes.title}</h2>
          </Reveal>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
          {UNIVERSE_META.map((u, i) => (
            <Reveal key={u.key} delay={i * 0.1}>
              <Link to={u.to} data-testid={`universe-${u.key}`} className="group block">
                <div className="relative overflow-hidden">
                  <img src={u.image} alt={c.universes[u.key].title} className="w-full aspect-[3/4] object-cover transition-transform duration-[900ms] ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-105" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                  <h3 className="absolute bottom-6 left-6 font-serif text-3xl !text-white">{c.universes[u.key].title}</h3>
                </div>
                <ul className="mt-5 space-y-1.5">
                  {c.universes[u.key].list.map((it) => <li key={it} className="text-sm text-olime-muted">{it}</li>)}
                </ul>
              </Link>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

const Philosophy = () => {
  const { c } = useLang();
  const p = c.philosophy;
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const imgScale = useTransform(scrollYProgress, [0, 1], [1.12, 1]);
  const icons = [Sparkles, HeartHandshake, Gem, Hourglass];
  return (
    <section ref={ref} data-testid="philosophy" className="py-28 lg:py-44 bg-olime-bg overflow-hidden">
      <div className="container-olime">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-14 lg:gap-20 items-center">
          <Reveal className="lg:col-span-5">
            <div className="relative overflow-hidden aspect-[4/5]">
              <motion.img style={{ scale: imgScale }} src={IMG.atelier} alt="O'Lime Design — matières, échantillons et inspirations d'atelier" className="w-full h-full object-cover" />
            </div>
          </Reveal>

          <div className="lg:col-span-7 lg:pl-6">
            <Reveal><span className="eyebrow">{p.eyebrow}</span></Reveal>
            <Reveal delay={0.05}>
              <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl tracking-tight mt-5 text-olime-ink uppercase">{p.title}</h2>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="mt-5 font-serif italic text-xl md:text-2xl text-olime-stone leading-snug max-w-xl">{p.subtitle}</p>
            </Reveal>
            <div className="mt-8 space-y-5 text-olime-muted leading-relaxed max-w-xl">
              {p.paragraphs.map((para, i) => (
                <Reveal key={i} delay={0.14 + i * 0.05}><p>{para}</p></Reveal>
              ))}
            </div>
          </div>
        </div>

        <div className="mt-20 lg:mt-28 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
          {p.values.map((v, i) => {
            const Icon = icons[i];
            return (
              <Reveal key={i} delay={i * 0.08}>
                <div data-testid={`philosophy-value-${i}`} className="h-full bg-white p-8 border border-olime-ink/[0.07] transition-transform duration-500 hover:-translate-y-1.5">
                  <Icon size={22} strokeWidth={1.3} className="text-olime-stone" />
                  <h3 className="font-serif text-lg mt-6 text-olime-ink">{v.title}</h3>
                  <p className="mt-3 text-sm text-olime-muted leading-relaxed">{v.text}</p>
                </div>
              </Reveal>
            );
          })}
        </div>

        <div className="mt-20 lg:mt-24 flex flex-col items-center text-center gap-9">
          <Reveal>
            <div>
              <p className="font-serif italic text-3xl text-olime-ink">{c.about.name}</p>
              <p className="mt-3 text-[11px] uppercase tracking-[0.28em] text-olime-muted">{p.signRole}</p>
              <p className="mt-1 text-[11px] uppercase tracking-[0.28em] text-olime-stone">O'LIME DESIGN</p>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <Link to="/services" data-testid="philosophy-services-cta" className="btn-ink">{p.cta} <ArrowRight size={14} /></Link>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

const PortfolioTeaser = () => {
  const { c } = useLang();
  return (
    <section data-testid="portfolio-teaser" className="py-24 lg:py-32 bg-olime-bg">
      <div className="container-olime">
        <div className="flex items-end justify-between mb-14">
          <Reveal><span className="eyebrow">{c.portfolio.eyebrow}</span>
            <h2 className="font-serif text-4xl md:text-6xl tracking-tight mt-3">{c.portfolio.title}</h2>
          </Reveal>
          <Reveal>
            <Link to="/portfolio" data-testid="portfolio-all-link" className="hidden md:inline-flex items-center gap-2 link-underline text-sm uppercase tracking-[0.2em]">{c.cta.seeAll} <ArrowRight size={14} /></Link>
          </Reveal>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-16">
          {PROJECTS.slice(0, 4).map((p, i) => (
            <Reveal key={p.slug} delay={(i % 2) * 0.1} className={i % 2 === 1 ? "md:mt-20" : ""}>
              <Link to={`/portfolio/${p.slug}`} data-testid={`project-${p.slug}`} className="group block">
                <div className="relative overflow-hidden">
                  <img src={p.image} alt={p.name} className="w-full aspect-[16/11] object-cover transition-transform duration-[900ms] ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-105" />
                </div>
                <div className="flex justify-between items-start mt-5">
                  <div>
                    <h3 className="font-serif text-3xl">{p.name}</h3>
                    <p className="text-sm text-olime-muted mt-1">{p.location} — {c.projects[p.slug].type}</p>
                  </div>
                  <span className="font-serif text-2xl text-olime-stone">{p.year}</span>
                </div>
                <span className="inline-flex items-center gap-2 mt-4 text-xs uppercase tracking-[0.2em] link-underline">{c.cta.discoverProject} <ArrowRight size={13} /></span>
              </Link>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

const Method = () => {
  const { c } = useLang();
  const nums = ["01", "02", "03", "04", "05", "06"];
  return (
    <section data-testid="method" className="py-24 lg:py-32 bg-white">
      <div className="container-olime">
        <Reveal><span className="eyebrow">{c.method.eyebrow}</span>
          <h2 className="font-serif text-4xl md:text-6xl tracking-tight mt-3 mb-16">{c.method.title}</h2>
        </Reveal>
        <div className="border-t border-olime-line">
          {c.method.steps.map((m, i) => (
            <Reveal key={i} delay={i * 0.06}>
              <div className="group grid grid-cols-12 gap-4 items-center py-8 border-b border-olime-line transition-colors duration-500 hover:bg-olime-bg">
                <span className="col-span-2 md:col-span-1 font-serif text-2xl text-olime-stone">{nums[i]}</span>
                <h3 className="col-span-10 md:col-span-4 font-serif text-2xl md:text-3xl">{m.title}</h3>
                <p className="col-span-12 md:col-span-6 text-olime-muted md:pl-8">{m.text}</p>
                <ArrowRight className="hidden md:block md:col-span-1 justify-self-end text-olime-stone transition-transform duration-500 group-hover:translate-x-1" size={18} />
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

const Why = () => {
  const { c } = useLang();
  return (
    <section data-testid="why" className="py-24 lg:py-32 bg-olime-manifesto">
      <div className="container-olime">
        <Reveal><div className="text-center mb-16"><span className="eyebrow">{c.why.eyebrow}</span>
          <h2 className="font-serif text-4xl md:text-5xl tracking-tight mt-3">{c.why.title}</h2></div></Reveal>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
          {c.why.items.map((w, i) => (
            <Reveal key={i} delay={i * 0.1}>
              <div className="text-center md:text-left">
                <span className="font-serif text-5xl text-olime-sand">0{i + 1}</span>
                <h3 className="font-serif text-3xl mt-4">{w.title}</h3>
                <p className="mt-4 text-olime-muted leading-relaxed">{w.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

const About = () => {
  const { c } = useLang();
  return (
    <section data-testid="about" className="py-24 lg:py-32 bg-olime-bg">
      <div className="container-olime grid grid-cols-1 lg:grid-cols-2 gap-14 lg:gap-24 items-center">
        <Reveal><div className="relative overflow-hidden"><img src={IMG.atelier} alt="O'Lime Design — matières & inspirations" loading="lazy" className="w-full aspect-[4/5] object-cover" /></div></Reveal>
        <div>
          <Reveal><span className="eyebrow">{c.about.eyebrow}</span>
            <h2 className="font-serif text-5xl md:text-6xl tracking-tight mt-4">{c.about.name}</h2>
            <p className="mt-3 text-olime-stone tracking-[0.15em] uppercase text-xs">{c.about.roles}</p>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="mt-8 space-y-5 text-olime-muted leading-relaxed max-w-xl"><p>{c.about.p1}</p><p>{c.about.p2}</p></div>
            <Link to="/studio" data-testid="about-studio-link" className="btn-outline mt-8">{c.cta.viewStudio} <ArrowRight size={14} /></Link>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

const Testimonials = () => {
  const { c } = useLang();
  return (
    <section data-testid="testimonials" className="py-24 lg:py-32 bg-olime-ink text-white">
      <div className="container-olime">
        <Reveal><span className="eyebrow text-white/50">{c.testimonials.eyebrow}</span></Reveal>
        <div className="mt-14 space-y-20">
          {c.testimonials.items.map((t, i) => (
            <Reveal key={i} delay={i * 0.05}>
              <div className={`grid grid-cols-1 lg:grid-cols-12 gap-8 items-center ${i % 2 ? "lg:[direction:rtl]" : ""}`}>
                <div className="lg:col-span-5 [direction:ltr]"><img src={TESTI_IMG[i]} alt={TESTI_META[i].author} className="w-full aspect-[4/3] object-cover" /></div>
                <div className="lg:col-span-7 [direction:ltr]">
                  <p className="font-serif text-3xl md:text-4xl leading-[1.25] italic !text-white">&ldquo;{t.quote}&rdquo;</p>
                  <p className="mt-6 text-olime-sand tracking-[0.2em] uppercase text-xs">{TESTI_META[i].author} — {TESTI_META[i].place}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

const JournalTeaser = () => {
  const { c } = useLang();
  const [posts, setPosts] = useState([]);
  useEffect(() => { api.get("/journal").then((r) => setPosts(r.data.slice(0, 3))).catch(() => {}); }, []);
  return (
    <section data-testid="journal-teaser" className="py-24 lg:py-32 bg-white">
      <div className="container-olime">
        <div className="flex items-end justify-between mb-14">
          <Reveal><span className="eyebrow">{c.journal.eyebrow}</span>
            <h2 className="font-serif text-4xl md:text-6xl tracking-tight mt-3">{c.journal.title}</h2></Reveal>
          <Reveal><Link to="/journal" data-testid="journal-all-link" className="hidden md:inline-flex items-center gap-2 link-underline text-sm uppercase tracking-[0.2em]">{c.cta.readAll} <ArrowRight size={14} /></Link></Reveal>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {posts.map((j, i) => (
            <Reveal key={j.slug} delay={i * 0.1}>
              <Link to={`/journal/${j.slug}`} data-testid={`journal-${j.slug}`} className="group block">
                <div className="overflow-hidden"><img src={j.image} alt={j.title} className="w-full aspect-[4/3] object-cover transition-transform duration-[900ms] ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-105" /></div>
                <p className="eyebrow mt-5">{j.category} • {j.date}</p>
                <h3 className="font-serif text-2xl mt-2 leading-snug">{j.title}</h3>
                <p className="text-sm text-olime-muted mt-2">{j.excerpt}</p>
              </Link>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

const BookSection = () => {
  const { c } = useLang();
  return (
    <section data-testid="book" className="py-24 lg:py-32 bg-olime-manifesto">
      <div className="container-olime grid grid-cols-1 lg:grid-cols-2 gap-14 lg:gap-20 items-center">
        <Reveal><div className="relative overflow-hidden"><img src={IMG.contact} alt={c.book.eyebrow} className="w-full aspect-[4/5] object-cover" /></div></Reveal>
        <div>
          <Reveal><span className="eyebrow">{c.book.eyebrow}</span>
            <h2 className="font-serif text-4xl md:text-5xl tracking-tight mt-4 mb-10">{c.book.title}</h2></Reveal>
          <Reveal delay={0.1}><ConsultationForm /></Reveal>
          <Reveal delay={0.15}><div className="mt-8"><BookConsultation variant="ink" testId="book-section-cta" /></div></Reveal>
        </div>
      </div>
    </section>
  );
};

export default function Home() {
  const { c, lang } = useLang();
  const { config } = useSiteConfig();
  return (
    <div data-testid="home-page">
      <SEO {...pageSEO("home", lang)} path="/" lang={lang} image={IMG.hero} jsonLd={businessSchema(config)} />
      <Hero />
      <Vision />
      <EditorialMarquee words={c.marquee1} />
      <Universes />
      <Philosophy />
      <PortfolioTeaser />
      <Method />
      <Why />
      <About />
      <Testimonials />
      <JournalTeaser />
      <BookSection />
    </div>
  );
}
